/**
 * PersonaIndicator Component
 * BRD-DEV-001 | SCR-DEV-002
 *
 * Floating badge showing current active persona in development mode.
 * Only visible when DEV_AUTH_BYPASS is enabled.
 *
 * Features:
 * - Collapsed state: Shows persona badge with color coding
 * - Expanded state: Dropdown with persona details and quick switch
 * - Click outside to close
 * - Escape key to close
 * - Quick persona switching without page reload
 * - Copy JWT token for API testing
 */

import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { UserIcon, ChevronUpIcon, XMarkIcon, KeyIcon, ArrowRightOnRectangleIcon } from '@heroicons/react/24/outline';
import { UserGroupIcon } from '@heroicons/react/24/solid';
import { Button } from '@myvault/shared/components/Button';
import { Badge } from '@myvault/shared/components/Badge';
import { Card } from '@myvault/shared/components/Card';
import { useToast } from '@myvault/shared/hooks';
import { cn } from '@myvault/shared/utils/cn';

// Persona configuration matching SCR-DEV-001
// BRD-019: Consolidated professional/intermediary into advisor
const PERSONA_CONFIG = {
  owner: {
    name: 'Owner',
    initials: 'O',
    email: 'dev-owner@myvault.dev',
    color: 'bg-purple-600',
    hoverColor: 'hover:bg-purple-700',
    textColor: 'text-purple-600',
    bgColor: 'bg-purple-50',
    badgeVariant: 'primary' as const,
    roles: ['owner', 'admin', 'editor', 'viewer'],
  },
  admin: {
    name: 'Admin',
    initials: 'A',
    email: 'dev-admin@myvault.dev',
    color: 'bg-red-600',
    hoverColor: 'hover:bg-red-700',
    textColor: 'text-red-600',
    bgColor: 'bg-red-50',
    badgeVariant: 'error' as const,
    roles: ['admin', 'editor', 'viewer'],
  },
  user: {
    name: 'User',
    initials: 'U',
    email: 'dev-user@myvault.dev',
    color: 'bg-blue-600',
    hoverColor: 'hover:bg-blue-700',
    textColor: 'text-blue-600',
    bgColor: 'bg-blue-50',
    badgeVariant: 'primary' as const,
    roles: ['editor', 'viewer'],
  },
  viewer: {
    name: 'Viewer',
    initials: 'V',
    email: 'dev-viewer@myvault.dev',
    color: 'bg-gray-600',
    hoverColor: 'hover:bg-gray-700',
    textColor: 'text-gray-600',
    bgColor: 'bg-gray-50',
    badgeVariant: 'default' as const,
    roles: ['viewer'],
  },
  advisor: {
    name: 'Advisor',
    initials: 'A',
    email: 'dev-advisor@myvault.dev',
    color: 'bg-green-600',
    hoverColor: 'hover:bg-green-700',
    textColor: 'text-green-600',
    bgColor: 'bg-green-50',
    badgeVariant: 'success' as const,
    roles: ['advisor', 'editor', 'viewer'],
  },
} as const;

type PersonaType = keyof typeof PERSONA_CONFIG;

interface PersonaStatusResponse {
  currentPersona: PersonaType | null;
  personaName: string;
  personaEmail: string;
  personaColor: string;
  roles: string[];
  tokenPreview: string;
}

interface PersonaAvatar {
  initials: string;
  color: string;
  size?: 'sm' | 'md' | 'lg';
}

function PersonaAvatarComponent({ initials, color, size = 'md' }: PersonaAvatar) {
  const sizeClasses = {
    sm: 'h-6 w-6 text-xs',
    md: 'h-10 w-10 text-sm',
    lg: 'h-12 w-12 text-base',
  };

  return (
    <div
      className={cn(
        'flex items-center justify-center rounded-full font-semibold text-white',
        color,
        sizeClasses[size]
      )}
    >
      {initials}
    </div>
  );
}

export function PersonaIndicator() {
  const [expanded, setExpanded] = useState(false);
  const [currentPersona, setCurrentPersona] = useState<PersonaType | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const toast = useToast();

  // Only show in development mode
  if (import.meta.env.PROD || import.meta.env.VITE_DEV_AUTH_BYPASS !== 'true') {
    return null;
  }

  // Fetch current persona on mount
  useEffect(() => {
    fetchCurrentPersona();
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setExpanded(false);
      }
    }

    function handleEscape(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        setExpanded(false);
      }
    }

    if (expanded) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleEscape);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
        document.removeEventListener('keydown', handleEscape);
      };
    }
  }, [expanded]);

  const fetchCurrentPersona = async () => {
    try {
      const response = await fetch('/api/v1/dev/status', {
        credentials: 'include',
      });

      if (!response.ok) {
        console.warn('[PersonaIndicator] Failed to fetch status');
        return;
      }

      const data: PersonaStatusResponse = await response.json();
      setCurrentPersona(data.currentPersona);
    } catch (error) {
      console.error('[PersonaIndicator] Error fetching status:', error);
    }
  };

  const handleSwitchPersona = async (persona: PersonaType) => {
    setIsLoading(true);
    setExpanded(false);

    try {
      const response = await fetch(`/api/v1/dev/switch/${persona}`, {
        method: 'POST',
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error(`Failed to switch persona: ${response.status}`);
      }

      const data = await response.json();

      // Store token in localStorage for useAuth hook to pick up
      // The useAuth hook reads from localStorage, not cookies
      if (data.token) {
        localStorage.setItem('myvault_access_token', data.token);
        localStorage.setItem('myvault_refresh_token', data.token); // Dev mode uses same token
      }

      // Update current persona
      setCurrentPersona(persona);

      // Show success toast
      toast.success('Persona switched', `Now logged in as ${PERSONA_CONFIG[persona].name}`);

      // Refresh the page to apply new persona
      window.location.reload();
    } catch (error) {
      console.error('[PersonaIndicator] Switch failed:', error);
      toast.error('Switch failed', error instanceof Error ? error.message : 'Unknown error');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCopyToken = async () => {
    if (!currentPersona) {
      toast.error('No persona selected', 'Please select a persona first');
      return;
    }

    try {
      const response = await fetch(`/api/v1/dev/token/${currentPersona}`, {
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error('Failed to get token');
      }

      const data = await response.json();
      await navigator.clipboard.writeText(data.token);

      toast.success('Token copied', 'JWT token copied to clipboard');
    } catch (error) {
      console.error('[PersonaIndicator] Copy token failed:', error);
      toast.error('Copy failed', 'Could not copy token to clipboard');
    }
  };

  const handleNavigateToSwitcher = () => {
    setExpanded(false);
    navigate('/dev');
  };

  const handleLogoutToPersonaSelection = () => {
    // Clear tokens to force re-authentication
    localStorage.removeItem('myvault_access_token');
    localStorage.removeItem('myvault_refresh_token');
    setExpanded(false);
    // Redirect to dev persona switcher (full page reload to clear state)
    window.location.href = '/dev';
  };

  const toggleExpanded = () => {
    setExpanded(!expanded);
  };

  if (!currentPersona) {
    return null;
  }

  const config = PERSONA_CONFIG[currentPersona];

  return (
    <div
      ref={dropdownRef}
      className="fixed bottom-6 right-6 z-[9999]"
      aria-label={`Current persona: ${config.name}. Click to expand options.`}
    >
      {!expanded ? (
        // Collapsed State
        <button
          onClick={toggleExpanded}
          className={cn(
            'flex items-center gap-2 rounded-full px-4 py-3 shadow-lg transition-all',
            config.color,
            config.hoverColor,
            'text-white font-semibold text-sm'
          )}
          aria-label={`Current persona: ${config.name}. Click to expand options.`}
        >
          <UserIcon className="h-4 w-4" />
          <span>{config.name}</span>
          <ChevronUpIcon className="h-3.5 w-3.5" />
        </button>
      ) : (
        // Expanded State
        <Card className="w-[280px] shadow-xl border-gray-200">
          {/* Header */}
          <div className={cn('rounded-t-lg p-4', config.color)}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-white font-semibold">
                  <span className={config.textColor}>{config.initials}</span>
                </div>
                <div>
                  <div className="font-semibold text-white">{config.name}</div>
                  <div className="text-xs text-white/80">{config.email}</div>
                </div>
              </div>
              <button
                onClick={toggleExpanded}
                className="rounded p-1 text-white/80 transition-colors hover:bg-white/20 hover:text-white"
                aria-label="Close persona dropdown"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>
          </div>

          {/* Role Badges */}
          <div className="border-b border-gray-200 p-3">
            <div className="flex flex-wrap gap-2">
              {config.roles.map((role) => (
                <Badge key={role} variant={config.badgeVariant}>
                  {role}
                </Badge>
              ))}
            </div>
          </div>

          {/* Quick Switch */}
          <div className="p-3">
            <div className="mb-2 text-xs font-medium text-gray-500">Quick Switch</div>
            <div className="grid grid-cols-3 gap-2">
              {(Object.keys(PERSONA_CONFIG) as PersonaType[]).map((persona) => {
                const personaConfig = PERSONA_CONFIG[persona];
                const isActive = currentPersona === persona;

                return (
                  <button
                    key={persona}
                    onClick={() => handleSwitchPersona(persona)}
                    disabled={isLoading || isActive}
                    className={cn(
                      'flex flex-col items-center gap-1 rounded-lg p-2 transition-all',
                      isActive
                        ? cn(personaConfig.color, 'text-white')
                        : 'bg-gray-50 text-gray-600 hover:bg-gray-100',
                      (isLoading || isActive) && 'cursor-not-allowed opacity-50'
                    )}
                    aria-label={`Switch to ${personaConfig.name} persona`}
                  >
                    <div
                      className={cn(
                        'flex h-6 w-6 items-center justify-center rounded-full text-xs font-semibold',
                        isActive ? 'bg-white text-gray-900' : cn(personaConfig.color, 'text-white')
                      )}
                    >
                      {personaConfig.initials}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Actions */}
          <div className="border-t border-gray-200 p-3">
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={handleNavigateToSwitcher}
                className="flex-1 text-xs"
              >
                <UserGroupIcon className="mr-1.5 h-4 w-4" />
                Persona Switcher
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={handleCopyToken}
                className="text-xs"
                aria-label="Copy JWT Token"
              >
                <KeyIcon className="h-4 w-4" />
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={handleLogoutToPersonaSelection}
                className="text-xs text-amber-600 hover:text-amber-700 hover:bg-amber-50"
                aria-label="Switch Persona (Logout)"
              >
                <ArrowRightOnRectangleIcon className="h-4 w-4" />
              </Button>
            </div>
          </div>

          {/* Dev Mode Warning */}
          <div className="rounded-b-lg bg-amber-50 px-3 py-2">
            <div className="flex items-center gap-2">
              <svg
                className="h-3.5 w-3.5 text-amber-600"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              </svg>
              <span className="text-xs font-medium text-amber-800">Development Mode</span>
            </div>
          </div>
        </Card>
      )}
    </div>
  );
}
